import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Receipt,
  Visibility,
  Edit,
  Cancel,
  CheckCircle,
  Pending,
} from "@mui/icons-material";
import { itemIssueService } from "../../services/itemIssueService";
import { useSnackbar } from "notistack";

const ActionDialogs = ({
  actionDialog,
  setActionDialog,
  handleConfirmCancel,
  handleConfirmApprove,
  handleConfirmIssue,
}) => {
  return (
    <>
      {/* Cancel ItemIssue Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "cancel"}
        onClose={() => setActionDialog({ open: false, type: "", itemIssue: null })}
      >
        <DialogTitle>Cancel Item Issue</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. Stock will be restored.
          </Alert>
          <Typography>
            Are you sure you want to cancel issue #
            {actionDialog.itemIssue?.issueNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            No, Keep It
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
          >
            Yes, Cancel Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve ItemIssue Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "approve"}
        onClose={() => setActionDialog({ open: false, type: "", itemIssue: null })}
      >
        <DialogTitle>Approve Item Issue</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Approving will allocate stock to the department.
          </Alert>
          <Typography>
            Are you sure you want to approve issue #
            {actionDialog.itemIssue?.issueNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmApprove}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Items Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "issue"}
        onClose={() => setActionDialog({ open: false, type: "", itemIssue: null })}
      >
        <DialogTitle>Issue Items</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Mark items as issued to the department.
          </Alert>
          <Typography>
            Are you sure you want to mark issue #
            {actionDialog.itemIssue?.issueNumber} as issued?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", itemIssue: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmIssue}
          >
            Mark as Issued
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ItemIssueList = () => {
  const [itemIssues, setItemIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItemIssue, setSelectedItemIssue] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "", // 'approve', 'cancel', 'issue'
    itemIssue: null,
  });

  useEffect(() => {
    fetchItemIssues();
  }, []);

  const fetchItemIssues = async () => {
    try {
      const response = await itemIssueService.getItemIssues();
      setItemIssues(response.data.data.itemIssues || []);
    } catch (error) {
      enqueueSnackbar("Error fetching item issues", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, itemIssue) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemIssue(itemIssue);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItemIssue(null);
  };

  const handleViewDetails = () => {
    navigate(`/hospital/hospital-inventory/item-issues/${selectedItemIssue._id}`);
    handleMenuClose();
  };

  const handleApproveItemIssue = () => {
    setActionDialog({
      open: true,
      type: "approve",
      itemIssue: selectedItemIssue,
    });
    handleMenuClose();
  };

  const handleIssueItems = () => {
    setActionDialog({
      open: true,
      type: "issue",
      itemIssue: selectedItemIssue,
    });
    handleMenuClose();
  };

  const handleCancelItemIssue = () => {
    setActionDialog({
      open: true,
      type: "cancel",
      itemIssue: selectedItemIssue,
    });
    handleMenuClose();
  };

  const handlePrintIssue = async () => {
    try {
      const response = await itemIssueService.getItemIssue(selectedItemIssue._id);
      const issue = response.data.data.itemIssue;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
      <html>
        <head>
          <title>Item Issue #${issue.issueNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .details { margin: 20px 0; }
            .table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .table th { background-color: #f5f5f5; }
            .totals { float: right; margin-top: 20px; }
            .footer { margin-top: 50px; border-top: 1px solid #333; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ITEM ISSUE</h1>
            <h2>#${issue.issueNumber}</h2>
          </div>
          
          <div class="details">
            <p><strong>Date:</strong> ${new Date(
              issue.issueDate
            ).toLocaleDateString()}</p>
            ${
              issue.department
                ? `<p><strong>Department:</strong> ${issue.department.department} (${issue.department.code})</p>`
                : ""
            }
            <p><strong>Purpose:</strong> ${issue.purpose}</p>
            <p><strong>Status:</strong> ${issue.status}</p>
            ${
              issue.requestedBy
                ? `<p><strong>Requested By:</strong> ${issue.requestedBy.name}</p>`
                : ""
            }
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Batch</th>
                <th>Qty</th>
                <th>Cost Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${issue.items
                .map(
                  (item) => `
                <tr>
                  <td>${item.item.name}</td>
                  <td>${item.batchNumber}</td>
                  <td>${item.quantity}</td>
                  <td>₹${item.costPrice}</td>
                  <td>₹${(item.quantity * item.costPrice).toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <p><strong>Subtotal: ₹${issue.subtotal.toFixed(2)}</strong></p>
            <p><strong>Tax: ₹${issue.taxAmount.toFixed(2)}</strong></p>
            <p><strong>Total: ₹${issue.grandTotal.toFixed(2)}</strong></p>
          </div>
          
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      enqueueSnackbar("Error generating issue document", { variant: "error" });
    }
    handleMenuClose();
  };

  const handleConfirmCancel = async () => {
    try {
      await itemIssueService.cancelItemIssue(actionDialog.itemIssue._id);
      enqueueSnackbar("Item issue cancelled successfully", { variant: "success" });
      fetchItemIssues(); // Refresh list
      setActionDialog({ open: false, type: "", itemIssue: null });
    } catch (error) {
      enqueueSnackbar("Error cancelling item issue", { variant: "error" });
    }
  };

  const handleConfirmApprove = async () => {
    try {
      await itemIssueService.approveItemIssue(actionDialog.itemIssue._id);
      enqueueSnackbar("Item issue approved successfully", { variant: "success" });
      fetchItemIssues(); // Refresh list
      setActionDialog({ open: false, type: "", itemIssue: null });
    } catch (error) {
      enqueueSnackbar("Error approving item issue", { variant: "error" });
    }
  };

  const handleConfirmIssue = async () => {
    try {
      await itemIssueService.issueItems(actionDialog.itemIssue._id);
      enqueueSnackbar("Items issued successfully", { variant: "success" });
      fetchItemIssues(); // Refresh list
      setActionDialog({ open: false, type: "", itemIssue: null });
    } catch (error) {
      enqueueSnackbar("Error issuing items", { variant: "error" });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      pending_approval: "warning",
      approved: "info",
      issued: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  const filteredItemIssues = itemIssues.filter(
    (issue) =>
      issue.issueNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.department &&
        issue.department.department
          .toLowerCase()
          .includes(searchTerm.toLowerCase()))
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Item Issues
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/hospital/hospital-inventory/item-issues/new")}
        >
          New Item Issue
        </Button>
      </Box>

      {/* Search Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by issue number or department name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Item Issues Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Receipt color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {itemIssues.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Issues
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {itemIssues.filter((i) => i.status === "pending_approval").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {itemIssues.filter((i) => i.status === "approved").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {itemIssues.filter((i) => i.status === "issued").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Issued
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Item Issues Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Recent Item Issues
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Issue #</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItemIssues.map((issue) => (
                  <TableRow key={issue._id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {issue.issueNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {issue.department ? (
                        <>
                          <Typography variant="body2">
                            {issue.department.department}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {issue.department.code}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Department
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.purpose}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(issue.issueDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(issue.issueDate).toLocaleTimeString()}
                      </Typography>
                      {issue.updatedAt !== issue.createdAt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Modified:{" "}
                          {new Date(issue.updatedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        ₹{issue.grandTotal.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={issue.status.replace("_", " ")}
                        color={getStatusColor(issue.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, issue)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredItemIssues.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No item issues found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={handleApproveItemIssue}
          disabled={selectedItemIssue?.status !== "pending_approval"}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Approve
        </MenuItem>
        <MenuItem
          onClick={handleIssueItems}
          disabled={selectedItemIssue?.status !== "approved"}
        >
          <Pending sx={{ mr: 1 }} />
          Mark as Issued
        </MenuItem>
        <MenuItem
          onClick={handleCancelItemIssue}
          disabled={
            selectedItemIssue?.status === "cancelled" ||
            selectedItemIssue?.status === "issued"
          }
        >
          <Cancel sx={{ mr: 1 }} />
          Cancel Issue
        </MenuItem>
        <MenuItem onClick={handlePrintIssue}>
          <Receipt sx={{ mr: 1 }} />
          Print Issue
        </MenuItem>
      </Menu>
      <ActionDialogs
        actionDialog={actionDialog}
        setActionDialog={setActionDialog}
        handleConfirmCancel={handleConfirmCancel}
        handleConfirmApprove={handleConfirmApprove}
        handleConfirmIssue={handleConfirmIssue}
      />
    </Box>
  );
};
